/**
 * Utility functions for formatting and rendering application letters
 * Used by TeacherDashboard and HeadteacherDashboard
 */

// Alignment/style/placeholder tags the renderers below actually understand.
// Anything bracket-shaped that ISN'T one of these is LLM drift (the model
// inventing a tag like "[BOTTOM RIGHT]") and gets stripped rather than shown
// to the user verbatim.
const KNOWN_LETTER_TAGS = /^\[\/?(RIGHT_ALIGN|RIGHT|LEFT|CENTER|BOLD_UNDERLINE|BOLD|UNDERLINE|SIGNATURE_PLACEHOLDER|SIGNATURE|STAMP_PLACEHOLDER|STAMP)\]$/i;

/**
 * Strip markdown code fences (including a language tag like ```plaintext)
 * and any unrecognized bracket-tag the LLM invented, before the letter is
 * rendered or converted to HTML.
 * @param {string} text
 * @returns {string}
 */
const stripLlmArtifacts = (text) => {
  if (!text) return text;
  return text
    // ``` or ```plaintext / ```markdown / etc, opening or closing fence
    .replace(/```[a-zA-Z]*\n?/g, '')
    .replace(/```/g, '')
    // A stray bracket tag on its own line that isn't one we know how to
    // render (e.g. "[BOTTOM RIGHT]") — drop the whole line's tag, keep any
    // other text on that line.
    .split('\n')
    .map((line) => {
      const trimmed = line.trim();
      if (/^\[[^\]]+\]$/.test(trimmed) && !KNOWN_LETTER_TAGS.test(trimmed)) {
        return '';
      }
      return line;
    })
    .join('\n');
};

/**
 * Format letter for display (strips technical markers)
 * @param {string} letter - The letter content with formatting tags
 * @returns {string} - The cleaned letter text
 */
export const formatLetterForDisplay = (letter) => {
  if (!letter) return '';
  
  let formatted = stripLlmArtifacts(letter)
    .replace(/\[RIGHT\]\s*/g, '')
    .replace(/\[LEFT\]\s*/g, '')
    .replace(/\[CENTER\]\s*/g, '')
    .replace(/\[BOLD_UNDERLINE\]/g, '')
    .replace(/\[\/BOLD_UNDERLINE\]/g, '')
    .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
    .replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '\n\n____________________\n(Signature)')
    .replace(/\[STAMP_PLACEHOLDER\]/gi, '\n\n[Official Stamp]')
    .replace(/\[SIGNATURE\]/gi, '\n\n____________________\n(Signature)')
    .replace(/\[STAMP\]/gi, '\n\n[Official Stamp]');
  
  return formatted.trim();
};

/**
 * Render formatted letter with proper styling for preview
 * @param {string} letter - The letter content with formatting tags
 * @param {string|null} signatureUrl - URL to the digital signature image
 * @param {string|null} stampUrl - URL to the official stamp image
 * @param {string} backendUrl - Backend URL for image paths (defaults to REACT_APP_BACKEND_URL)
 * @returns {JSX.Element[]} - Array of React elements for rendering
 */
export const renderFormattedLetter = (letter, signatureUrl = null, stampUrl = null, backendUrl = null) => {
  const React = require('react');
  
  if (!letter) return null;
  
  const resolvedBackendUrl = backendUrl || process.env.REACT_APP_BACKEND_URL || '';
  letter = stripLlmArtifacts(letter);
  const lines = letter.split('\n');
  const elements = [];
  let currentAlignment = 'left';
  
  lines.forEach((line, index) => {
    let processedLine = line;
    let lineAlignment = currentAlignment;
    let isHeading = false;
    let isBold = false;
    let isUnderline = false;
    
    // Check for alignment markers and update current alignment state
    if (processedLine.includes('[RIGHT]') || processedLine.includes('[RIGHT_ALIGN]')) {
      lineAlignment = 'right';
      currentAlignment = 'right';
      processedLine = processedLine.replace(/\[RIGHT\]\s*/g, '').replace(/\[RIGHT_ALIGN\]\s*/g, '');
    } else if (processedLine.includes('[LEFT]')) {
      lineAlignment = 'left';
      currentAlignment = 'left';
      processedLine = processedLine.replace(/\[LEFT\]\s*/g, '');
    } else if (processedLine.includes('[CENTER]')) {
      lineAlignment = 'center';
      currentAlignment = 'center';
      processedLine = processedLine.replace(/\[CENTER\]\s*/g, '');
    }
    
    // Detect signatory/closing lines and force right alignment
    const trimmedLower = processedLine.trim().toLowerCase();
    const isSignatoryLine = (
      trimmedLower.startsWith('yours faithfully') ||
      trimmedLower.startsWith('yours sincerely') ||
      trimmedLower.startsWith('yours truly') ||
      trimmedLower.startsWith('respectfully yours') ||
      trimmedLower.startsWith('with regards') ||
      trimmedLower.startsWith('best regards') ||
      trimmedLower.startsWith('kind regards') ||
      trimmedLower === 'yours faithfully,' ||
      trimmedLower === 'yours sincerely,' ||
      trimmedLower === 'yours truly,'
    );
    
    if (isSignatoryLine) {
      lineAlignment = 'right';
      currentAlignment = 'right'; // All subsequent lines should also be right-aligned
    }
    
    // Check for bold/underline markers (both opening and closing)
    if (processedLine.includes('[BOLD_UNDERLINE]') || processedLine.includes('[/BOLD_UNDERLINE]')) {
      isHeading = processedLine.includes('[BOLD_UNDERLINE]');
      processedLine = processedLine.replace(/\[\/?BOLD_UNDERLINE\]/g, '');
    }
    
    // Handle inline [BOLD] tags
    if (processedLine.includes('[BOLD]') || processedLine.includes('[/BOLD]')) {
      isBold = true;
      processedLine = processedLine.replace(/\[\/?BOLD\]/g, '');
    }
    
    // Handle inline [UNDERLINE] tags
    if (processedLine.includes('[UNDERLINE]') || processedLine.includes('[/UNDERLINE]')) {
      isUnderline = true;
      processedLine = processedLine.replace(/\[\/?UNDERLINE\]/g, '');
    }
    
    // Clean up closing alignment tags
    processedLine = processedLine.replace(/\[\/?CENTER\]/g, '').replace(/\[\/?RIGHT_ALIGN\]/g, '').replace(/\[\/?RIGHT\]/g, '');
    
    // Also detect heading by pattern: centered line that looks like a subject heading
    if (!isHeading && lineAlignment === 'center') {
      const upperLine = processedLine.toUpperCase().trim();
      if (upperLine.startsWith('APPLICATION') || 
          upperLine.startsWith('RE:') || 
          upperLine.startsWith('SUBJECT:') ||
          upperLine.startsWith('MEMORANDUM') ||
          (upperLine === processedLine.trim() && upperLine.length > 10 && upperLine.length < 100)) {
        isHeading = true;
      }
    }
    
    // Handle signature placeholder - show actual signature if available
    if (/\[SIGNATURE_PLACEHOLDER\]|\[SIGNATURE\]/i.test(processedLine)) {
      currentAlignment = 'right';
      
      if (signatureUrl) {
        elements.push(
          React.createElement('div', { key: index, className: 'flex justify-end mt-4 mb-2' },
            React.createElement('img', {
              src: `${resolvedBackendUrl}${signatureUrl}`,
              alt: 'Digital Signature',
              className: 'h-16 w-auto object-contain',
              onError: (e) => {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'block';
                }
              }
            }),
            React.createElement('div', { className: 'hidden' },
              React.createElement('div', { className: 'border-b-2 border-gray-400 w-48 mb-1' }),
              React.createElement('div', { className: 'text-sm text-gray-500 italic' }, '(Signature)')
            )
          )
        );
      } else {
        elements.push(
          React.createElement('div', { key: index, className: 'flex justify-end mt-4 mb-2' },
            React.createElement('div', null,
              React.createElement('div', { className: 'border-b-2 border-gray-400 w-48 mb-1' }),
              React.createElement('div', { className: 'text-sm text-gray-500 italic' }, '(Signature - upload in Profile)')
            )
          )
        );
      }
      return;
    }
    
    // Handle stamp placeholder - show actual stamp if available
    if (/\[STAMP_PLACEHOLDER\]|\[STAMP\]/i.test(processedLine)) {
      if (stampUrl) {
        elements.push(
          React.createElement('div', { key: index, className: 'flex justify-end my-2' },
            React.createElement('img', {
              src: `${resolvedBackendUrl}${stampUrl}`,
              alt: 'Official Stamp',
              className: 'h-20 w-auto object-contain'
            })
          )
        );
      } else {
        elements.push(
          React.createElement('div', { key: index, className: 'flex justify-end my-2' },
            React.createElement('div', { className: 'border-2 border-dashed border-gray-300 px-4 py-2 text-sm text-gray-400' },
              '[Official Stamp - upload in Profile]'
            )
          )
        );
      }
      return;
    }
    
    // Remove any remaining markers including code fence, plaintext, and closing alignment tags
    processedLine = processedLine
      .replace(/```/g, '')
      .replace(/`plaintext`?/gi, '')
      .replace(/plaintext/gi, '')
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?CENTER\]/g, '')
      .replace(/\[\/?RIGHT\]/g, '')
      .replace(/\[\/?LEFT\]/g, '')
      .replace(/\[\/?RIGHT_ALIGN\]/g, '')
      .replace(/\[\/?BOLD\]/g, '')
      .replace(/\[\/?UNDERLINE\]/g, '')
      .trim();
    
    // Skip empty lines but add spacing
    if (!processedLine) {
      elements.push(React.createElement('div', { key: index, className: 'h-3' }));
      return;
    }
    
    // Determine alignment classes
    let containerClass = 'flex';
    if (lineAlignment === 'right') {
      containerClass = 'flex justify-end';
    } else if (lineAlignment === 'center') {
      containerClass = 'flex justify-center';
    } else {
      containerClass = 'flex justify-start';
    }
    
    // Build style object for formatting
    const textStyle = {};
    if (isBold || isHeading) {
      textStyle.fontWeight = 'bold';
    }
    if (isUnderline || isHeading) {
      textStyle.textDecoration = 'underline';
      textStyle.textUnderlineOffset = '4px';
    }
    if (isHeading) {
      textStyle.textTransform = 'uppercase';
      textStyle.fontSize = '1rem';
    }
    
    if (isHeading) {
      elements.push(
        React.createElement('div', { key: index, className: 'flex justify-center my-4' },
          React.createElement('span', { style: textStyle }, processedLine)
        )
      );
    } else {
      elements.push(
        React.createElement('div', { key: index, className: containerClass },
          React.createElement('span', { style: Object.keys(textStyle).length > 0 ? textStyle : undefined }, processedLine)
        )
      );
    }
  });
  
  return elements;
};

/**
 * Convert structured text (with tags like [CENTER], [BOLD]) to HTML for TipTap editor
 * @param {string} text - The structured text with formatting tags
 * @returns {string} - HTML string for the editor
 */
export const convertStructuredTextToHtml = (text) => {
  if (!text) return '';
  
  let html = text
    .split('\n\n')
    .map(para => {
      let processed = para
        // Handle CENTER tags
        .replace(/\[CENTER\](.*?)\[\/CENTER\]/gs, '<p style="text-align: center;">$1</p>')
        .replace(/\[CENTER\]\s*/g, '<p style="text-align: center;">')
        // Handle BOLD tags
        .replace(/\[BOLD\](.*?)\[\/BOLD\]/g, '<strong>$1</strong>')
        .replace(/\[BOLD_UNDERLINE\](.*?)\[\/BOLD_UNDERLINE\]/g, '<strong><u>$1</u></strong>')
        .replace(/\[BOLD_UNDERLINE\]/g, '<strong><u>')
        .replace(/\[\/BOLD_UNDERLINE\]/g, '</u></strong>')
        // Handle UNDERLINE tags
        .replace(/\[UNDERLINE\](.*?)\[\/UNDERLINE\]/g, '<u>$1</u>')
        // Handle RIGHT_ALIGN tags
        .replace(/\[RIGHT_ALIGN\](.*?)\[\/RIGHT_ALIGN\]/gs, '<p style="text-align: right;">$1</p>')
        .replace(/\[RIGHT_ALIGN\]\s*/g, '<p style="text-align: right;">')
        // Clean up remaining tags
        .replace(/\[\/?CENTER\]/g, '')
        .replace(/\[\/?BOLD\]/g, '')
        .replace(/\[\/?UNDERLINE\]/g, '')
        .replace(/\[\/?RIGHT_ALIGN\]/g, '');
      
      // Wrap in paragraph if not already wrapped
      if (!processed.startsWith('<p')) {
        processed = `<p>${processed}</p>`;
      }
      return processed;
    })
    .join('');
  
  // Handle single line breaks within paragraphs
  html = html.replace(/\n/g, '<br/>');
  
  return html;
};

/**
 * Convert HTML back to structured text for PDF generation
 * @param {string} html - HTML string from TipTap editor
 * @returns {string} - Structured text with formatting tags
 */
export const convertHtmlToStructuredText = (html) => {
  if (!html) return '';
  
  // Create a temporary DOM element to parse HTML
  const div = document.createElement('div');
  div.innerHTML = html;
  
  const processNode = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent;
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      let content = Array.from(node.childNodes).map(processNode).join('');
      
      // Handle text alignment
      const style = node.getAttribute('style') || '';
      if (style.includes('text-align: center')) {
        content = `[CENTER]${content}[/CENTER]`;
      } else if (style.includes('text-align: right')) {
        content = `[RIGHT_ALIGN]${content}[/RIGHT_ALIGN]`;
      }
      
      // Handle formatting
      if (tag === 'strong' || tag === 'b') {
        content = `[BOLD]${content}[/BOLD]`;
      }
      if (tag === 'u') {
        content = `[UNDERLINE]${content}[/UNDERLINE]`;
      }
      if (tag === 'p') {
        content = content + '\n\n';
      }
      if (tag === 'br') {
        content = '\n';
      }
      
      return content;
    }
    
    return '';
  };
  
  let text = Array.from(div.childNodes).map(processNode).join('');
  
  // Clean up extra whitespace
  text = text.replace(/\n{3,}/g, '\n\n').trim();
  
  return text;
};

/**
 * Convert memo with formatting tags to HTML for print/PDF output (includes stamp support)
 * @param {string} memo - The memo content with formatting tags
 * @param {string|null} signatureUrl - URL to the digital signature image
 * @param {string|null} stampUrl - URL to the official stamp image
 * @param {string} backendUrl - Backend URL for image paths (defaults to REACT_APP_BACKEND_URL)
 * @returns {string} - HTML string ready for print view
 */
export const convertMemoToFormattedHtml = (memo, signatureUrl = null, stampUrl = null, backendUrl = null) => {
  if (!memo) return '';
  
  const resolvedBackendUrl = backendUrl || process.env.REACT_APP_BACKEND_URL || '';
  memo = stripLlmArtifacts(memo);
  // If the text already carries an explicit [SIGNATURE_PLACEHOLDER]/[SIGNATURE]
  // marker, that line alone inserts the signature below — don't ALSO auto-insert
  // one right after "Yours faithfully,", or it renders twice.
  const hasExplicitSignatureMarker = /\[SIGNATURE_PLACEHOLDER\]|\[SIGNATURE\]/i.test(memo);
  const lines = memo.split('\n');
  let htmlLines = [];
  let currentAlignment = 'left';
  let inSignatoryBlock = false;
  let consecutiveEmptyLines = 0;
  
  lines.forEach((line, index) => {
    let processedLine = line;
    let lineAlignment = currentAlignment;
    let isBold = false;
    let isUnderline = false;
    let isHeading = false;
    
    // Check for alignment markers
    if (processedLine.includes('[RIGHT]') || processedLine.includes('[RIGHT_ALIGN]')) {
      lineAlignment = 'right';
      currentAlignment = 'right';
      processedLine = processedLine.replace(/\[RIGHT\]\s*/g, '').replace(/\[RIGHT_ALIGN\]\s*/g, '');
    } else if (processedLine.includes('[LEFT]')) {
      lineAlignment = 'left';
      currentAlignment = 'left';
      processedLine = processedLine.replace(/\[LEFT\]\s*/g, '');
    } else if (processedLine.includes('[CENTER]')) {
      lineAlignment = 'center';
      currentAlignment = 'center';
      processedLine = processedLine.replace(/\[CENTER\]\s*/g, '');
    }
    
    // Detect signatory block
    const trimmedLower = processedLine.trim().toLowerCase();
    const isClosingLine = (
      trimmedLower.startsWith('yours faithfully') ||
      trimmedLower.startsWith('yours sincerely') ||
      trimmedLower.startsWith('yours truly') ||
      trimmedLower.startsWith('respectfully yours') ||
      trimmedLower.startsWith('best regards') ||
      trimmedLower.startsWith('kind regards')
    );
    
    if (isClosingLine) {
      inSignatoryBlock = true;
      lineAlignment = 'right';
      currentAlignment = 'right';
    }
    
    // Handle BOLD_UNDERLINE (heading style)
    if (processedLine.includes('[BOLD_UNDERLINE]')) {
      isHeading = true;
      processedLine = processedLine.replace(/\[\/?BOLD_UNDERLINE\]/g, '');
    }
    
    // Auto-detect headings
    if (!isHeading && lineAlignment === 'center') {
      const upperTrimmed = processedLine.trim().toUpperCase();
      if (upperTrimmed.startsWith('APPLICATION') ||
          upperTrimmed.startsWith('RE:') ||
          upperTrimmed.startsWith('SUBJECT:') ||
          upperTrimmed.startsWith('MEMORANDUM') ||
          upperTrimmed.startsWith('REQUEST') ||
          upperTrimmed.startsWith('LETTER OF')) {
        isHeading = true;
      }
    }
    
    // Handle inline [BOLD] tags
    if (processedLine.includes('[BOLD]') || processedLine.includes('[/BOLD]')) {
      isBold = true;
      processedLine = processedLine.replace(/\[\/?BOLD\]/g, '');
    }
    
    // Handle inline [UNDERLINE] tags
    if (processedLine.includes('[UNDERLINE]') || processedLine.includes('[/UNDERLINE]')) {
      isUnderline = true;
      processedLine = processedLine.replace(/\[\/?UNDERLINE\]/g, '');
    }
    
    // Clean up remaining tags
    processedLine = processedLine.replace(/\[\/?CENTER\]/g, '').replace(/\[\/?RIGHT_ALIGN\]/g, '').replace(/\[\/?RIGHT\]/g, '').replace(/\[\/?LEFT\]/g, '');
    
    // Handle signature placeholder - include actual signature image
    if (/\[SIGNATURE_PLACEHOLDER\]|\[SIGNATURE\]/i.test(processedLine)) {
      if (signatureUrl) {
        htmlLines.push(`<div class="text-right"><img src="${resolvedBackendUrl}${signatureUrl}" class="signature-img" alt="Signature" style="margin-left: auto; max-height: 60px;" /></div>`);
      } else {
        htmlLines.push(`<div class="text-right"><div class="signature-line"></div><em>(Signature)</em></div>`);
      }
      return;
    }
    
    // Handle stamp placeholder - include actual stamp image
    if (/\[STAMP_PLACEHOLDER\]|\[STAMP\]/i.test(processedLine)) {
      if (stampUrl) {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.3em;"><img src="${resolvedBackendUrl}${stampUrl}" alt="Official Stamp" style="margin-left: auto; max-height: 80px;" /></div>`);
      } else {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.3em;"><em>[Official Stamp - upload in Profile]</em></div>`);
      }
      return;
    }
    
    // Clean any remaining markers
    processedLine = processedLine.replace(/```/g, '').trim();
    
    // If closing line, add signature and stamp after it (unless an explicit
    // placeholder marker elsewhere in the text will already do this)
    if (isClosingLine && processedLine && !hasExplicitSignatureMarker) {
      htmlLines.push(`<div class="line text-right">${processedLine}</div>`);
      // Add signature
      if (signatureUrl) {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.3em;"><img src="${resolvedBackendUrl}${signatureUrl}" class="signature-img" alt="Signature" style="margin-left: auto; max-height: 60px;" /></div>`);
      } else {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.5em;"><div class="signature-line"></div></div>`);
      }
      // Add stamp
      if (stampUrl) {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.3em;"><img src="${resolvedBackendUrl}${stampUrl}" alt="Official Stamp" style="margin-left: auto; max-height: 80px;" /></div>`);
      }
      return;
    }
    
    // Handle empty lines
    if (!processedLine) {
      consecutiveEmptyLines++;
      if (consecutiveEmptyLines === 1) {
        htmlLines.push('<div class="section-break"></div>');
      }
      return;
    } else {
      consecutiveEmptyLines = 0;
    }
    
    // Build the HTML for this line
    let alignClass = 'text-left';
    if (lineAlignment === 'right' || inSignatoryBlock) {
      alignClass = 'text-right';
    } else if (lineAlignment === 'center') {
      alignClass = 'text-center';
    }
    
    let content = processedLine;
    
    if (isHeading) {
      htmlLines.push(`<div class="heading">${content}</div>`);
    } else {
      if (isBold) content = `<strong>${content}</strong>`;
      if (isUnderline) content = `<u>${content}</u>`;
      htmlLines.push(`<div class="line ${alignClass}">${content}</div>`);
    }
  });
  
  return htmlLines.join('\n');
};

/**
 * Convert letter with formatting tags to HTML for print/PDF output
 * @param {string} letter - The letter content with formatting tags
 * @param {string|null} signatureUrl - URL to the digital signature image
 * @param {string} backendUrl - Backend URL for image paths (defaults to REACT_APP_BACKEND_URL)
 * @returns {string} - HTML string ready for print view
 */
export const convertLetterToFormattedHtml = (letter, signatureUrl = null, backendUrl = null) => {
  if (!letter) return '';
  
  const resolvedBackendUrl = backendUrl || process.env.REACT_APP_BACKEND_URL || '';
  letter = stripLlmArtifacts(letter);
  // If the text already carries an explicit [SIGNATURE_PLACEHOLDER]/[SIGNATURE]
  // marker, that line alone inserts the signature below — don't ALSO auto-insert
  // one right after "Yours faithfully,", or it renders twice.
  const hasExplicitSignatureMarker = /\[SIGNATURE_PLACEHOLDER\]|\[SIGNATURE\]/i.test(letter);
  const lines = letter.split('\n');
  let htmlLines = [];
  let currentAlignment = 'left';
  let inSignatoryBlock = false;
  let consecutiveEmptyLines = 0;
  
  lines.forEach((line, index) => {
    let processedLine = line;
    let lineAlignment = currentAlignment;
    let isBold = false;
    let isUnderline = false;
    let isHeading = false;
    
    // Check for alignment markers
    if (processedLine.includes('[RIGHT]') || processedLine.includes('[RIGHT_ALIGN]')) {
      lineAlignment = 'right';
      currentAlignment = 'right';
      processedLine = processedLine.replace(/\[RIGHT\]\s*/g, '').replace(/\[RIGHT_ALIGN\]\s*/g, '');
    } else if (processedLine.includes('[LEFT]')) {
      lineAlignment = 'left';
      currentAlignment = 'left';
      processedLine = processedLine.replace(/\[LEFT\]\s*/g, '');
    } else if (processedLine.includes('[CENTER]')) {
      lineAlignment = 'center';
      currentAlignment = 'center';
      processedLine = processedLine.replace(/\[CENTER\]\s*/g, '');
    }
    
    // Detect signatory block and add signature line after "Yours faithfully"
    const trimmedLower = processedLine.trim().toLowerCase();
    const isClosingLine = (
      trimmedLower.startsWith('yours faithfully') ||
      trimmedLower.startsWith('yours sincerely') ||
      trimmedLower.startsWith('yours truly') ||
      trimmedLower.startsWith('respectfully yours') ||
      trimmedLower.startsWith('best regards') ||
      trimmedLower.startsWith('kind regards')
    );
    
    if (isClosingLine) {
      inSignatoryBlock = true;
      lineAlignment = 'right';
      currentAlignment = 'right';
    }
    
    // Handle BOLD_UNDERLINE (heading style)
    if (processedLine.includes('[BOLD_UNDERLINE]')) {
      isHeading = true;
      processedLine = processedLine.replace(/\[\/?BOLD_UNDERLINE\]/g, '');
    }
    
    // Auto-detect headings: centered text that looks like a title
    if (!isHeading && lineAlignment === 'center') {
      const upperTrimmed = processedLine.trim().toUpperCase();
      if (upperTrimmed.startsWith('APPLICATION') ||
          upperTrimmed.startsWith('RE:') ||
          upperTrimmed.startsWith('SUBJECT:') ||
          upperTrimmed.startsWith('MEMORANDUM') ||
          upperTrimmed.startsWith('REQUEST') ||
          upperTrimmed.startsWith('LETTER OF')) {
        isHeading = true;
      }
    }
    
    // Handle inline [BOLD] tags
    if (processedLine.includes('[BOLD]') || processedLine.includes('[/BOLD]')) {
      isBold = true;
      processedLine = processedLine.replace(/\[\/?BOLD\]/g, '');
    }
    
    // Handle inline [UNDERLINE] tags
    if (processedLine.includes('[UNDERLINE]') || processedLine.includes('[/UNDERLINE]')) {
      isUnderline = true;
      processedLine = processedLine.replace(/\[\/?UNDERLINE\]/g, '');
    }
    
    // Clean up remaining tags
    processedLine = processedLine.replace(/\[\/?CENTER\]/g, '').replace(/\[\/?RIGHT_ALIGN\]/g, '').replace(/\[\/?RIGHT\]/g, '').replace(/\[\/?LEFT\]/g, '');
    
    // Handle signature placeholder
    if (/\[SIGNATURE_PLACEHOLDER\]|\[SIGNATURE\]/i.test(processedLine)) {
      if (signatureUrl) {
        htmlLines.push(`<div class="text-right"><img src="${resolvedBackendUrl}${signatureUrl}" class="signature-img" alt="Signature" style="margin-left: auto;" /></div>`);
      } else {
        htmlLines.push(`<div class="text-right"><div class="signature-line"></div><em>(Signature)</em></div>`);
      }
      return;
    }
    
    // Handle stamp placeholder
    if (/\[STAMP_PLACEHOLDER\]|\[STAMP\]/i.test(processedLine)) {
      return; // Skip stamp for print
    }
    
    // Clean any remaining markers
    processedLine = processedLine.replace(/```/g, '').trim();
    
    // If this is a closing line (Yours faithfully), add the signature line after it
    // (unless an explicit placeholder marker elsewhere in the text will already do this)
    if (isClosingLine && processedLine && !hasExplicitSignatureMarker) {
      // Add the closing line
      htmlLines.push(`<div class="line text-right">${processedLine}</div>`);
      // Add signature line
      if (signatureUrl) {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.3em;"><img src="${resolvedBackendUrl}${signatureUrl}" class="signature-img" alt="Signature" style="margin-left: auto;" /></div>`);
      } else {
        htmlLines.push(`<div class="text-right" style="margin-top: 0.5em;"><div class="signature-line"></div></div>`);
      }
      return;
    }
    
    // Handle empty lines - only add one section break for multiple empty lines
    if (!processedLine) {
      consecutiveEmptyLines++;
      if (consecutiveEmptyLines === 1) {
        htmlLines.push('<div class="section-break"></div>');
      }
      return;
    } else {
      consecutiveEmptyLines = 0;
    }
    
    // Build the HTML for this line
    let alignClass = 'text-left';
    if (lineAlignment === 'right' || inSignatoryBlock) {
      alignClass = 'text-right';
    } else if (lineAlignment === 'center') {
      alignClass = 'text-center';
    }
    
    let content = processedLine;
    
    if (isHeading) {
      htmlLines.push(`<div class="heading">${content}</div>`);
    } else {
      if (isBold) content = `<strong>${content}</strong>`;
      if (isUnderline) content = `<u>${content}</u>`;
      htmlLines.push(`<div class="line ${alignClass}">${content}</div>`);
    }
  });
  
  return htmlLines.join('\n');
};

export default {
  formatLetterForDisplay,
  renderFormattedLetter,
  convertLetterToFormattedHtml,
  convertMemoToFormattedHtml,
  convertStructuredTextToHtml,
  convertHtmlToStructuredText
};
